import React from 'react';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Shield } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

const GAPInsuranceFAQPage = () => {
  const faqs = [
    {
      question: "What is GAP insurance and why was it mis-sold?",
      answer: "GAP insurance covers the difference between what your car insurer pays out if your vehicle is written off, and what you still owe on finance. The product itself isn't the problem — it's how it was sold. Dealers were earning up to 70% of premiums as commission without telling customers. This hidden commission, combined with pressure selling and failure to explain you could buy cheaper cover elsewhere, constitutes mis-selling."
    },
    {
      question: "How do I know if my GAP insurance was mis-sold?",
      answer: "If you bought GAP insurance from a car dealer and weren't told: (1) how much commission the dealer was earning, (2) that GAP was optional, (3) that you could buy it elsewhere for a fraction of the price, or (4) about the 14-day cooling-off period — then your policy was likely mis-sold. The FCA has found these failures were widespread across the industry."
    },
    {
      question: "Why are 80% of GAP insurance providers no longer selling?",
      answer: "Following an FCA review that found only 6% of premiums were paid out in claims while up to 70% went to dealers as commission, the regulator demanded providers prove their products offer 'fair value'. Unable to do so, 80% of providers voluntarily suspended sales. This is a tacit admission that their sales practices were problematic."
    },
    {
      question: "How much compensation could I receive?",
      answer: "Compensation depends on individual circumstances including what you paid for the policy, the commission level, and how the sale was conducted. Some claimants may receive a full refund of premiums plus interest. We assess each case individually and cannot guarantee specific outcomes, but the widespread nature of mis-selling means many claims are strong."
    },
    {
      question: "I don't have my GAP insurance documents. Can I still claim?",
      answer: "Yes. Many people don't keep insurance documents for years. Blue Lion Law can request the necessary information from finance companies and dealers as part of the claims process. You don't need everything in hand to get started — just basic details about when you bought the car and where."
    },
    {
      question: "Is this like the PPI scandal?",
      answer: "The parallels are striking. Like PPI, GAP insurance was pushed with hidden commissions, inadequate disclosure, and pressure selling. The same finance industry that paid out billions for PPI continued similar practices with GAP insurance. The key difference is that the FCA has acted more quickly this time — but that also means there's no formal deadline yet, so acting early is important."
    },
    {
      question: "What if I've already finished paying for my car finance?",
      answer: "You can still claim. The mis-selling occurred at the point of sale, not during the policy period. Whether your finance is paid off, you've changed cars, or you never claimed on the policy — none of this prevents you from seeking compensation for how the GAP insurance was sold to you."
    },
    {
      question: "Can I claim for my car finance AND my GAP insurance?",
      answer: "Yes, potentially both. Many consumers were hit twice — first with hidden commission on PCP/HP finance (Discretionary Commission Arrangements), then again with hidden commission on GAP insurance sold alongside it. Blue Lion Law can assess both claims together to ensure you don't miss any compensation you may be owed."
    },
    {
      question: "What does 'no win, no fee' actually mean?",
      answer: "It means if your claim is unsuccessful, you pay nothing for our legal services. If your claim succeeds, our fee is a percentage of the compensation recovered — you'll know this percentage upfront before proceeding. There are no hidden costs or surprises."
    },
    {
      question: "How long does the claims process take?",
      answer: "Timescales vary depending on case complexity and how quickly the other parties respond. Some claims resolve in months; others take longer. We keep you informed throughout and pursue your claim as efficiently as possible. The FCA's ongoing review may also affect industry-wide timelines."
    },
    {
      question: "Is there a deadline to claim?",
      answer: "There is no formal FCA deadline yet for GAP insurance claims. However, general limitation periods apply (typically 6 years from the event, or 3 years from when you became aware of the problem). More importantly, don't wait for a deadline — with PPI, millions missed out because they delayed. Act now while the window is clearly open."
    },
    {
      question: "Who is Blue Lion Law?",
      answer: "Blue Lion Law Limited is a UK law firm authorised and regulated by the Solicitors Regulation Authority (SRA No: 815166). We specialise in financial mis-selling claims including GAP insurance and car finance. We're bound by strict professional standards and legal ethics in everything we do."
    }
  ];

  // FAQ Schema for SEO
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      {/* FAQ Schema Script */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-20 lg:py-28 bg-primary">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-6">
                <Shield className="w-4 h-4 text-gold" />
                <span className="text-sm font-medium text-gold">
                  Blue Lion Law
                </span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                GAP Insurance Claims: Your Questions Answered
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                Everything you need to know about mis-sold GAP insurance and how to claim
              </p>
            </div>
          </div>
        </section>

        {/* Intro Section */}
        <section className="py-12 bg-muted/30">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-lg text-muted-foreground">
                The GAP insurance scandal has exposed how dealers earned up to <strong>70% of 
                premiums in hidden commission</strong> while consumers were kept in the dark. 
                The FCA has acted, 80% of providers have suspended sales, and now millions may 
                be entitled to compensation.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 lg:py-24 bg-background">
          <div className="section-container">
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="bg-card border border-border rounded-xl px-6 data-[state=open]:shadow-md transition-shadow"
                  >
                    <AccordionTrigger className="text-left text-base font-semibold text-foreground hover:no-underline py-5">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* CTA */}
              <div className="mt-12 p-8 rounded-2xl bg-primary text-center">
                <h2 className="font-heading text-xl font-bold text-primary-foreground mb-4">
                  Ready to Check Your Claim?
                </h2>
                <p className="text-primary-foreground/80 mb-6">
                  Don't wait for a deadline to be announced. With PPI, millions missed out 
                  because they delayed. Act now while the window is open.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button variant="gold" size="lg" asChild>
                    <a href="/gap-insurance-claims">
                      Start Your Assessment
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </a>
                  </Button>
                  <Button variant="ghost-light" size="lg" className="border border-primary-foreground/30" asChild>
                    <a href="tel:02081530033">
                      Call 0208 153 0033
                    </a>
                  </Button>
                </div>
              </div>

              {/* Related Links */}
              <div className="mt-12">
                <h3 className="font-heading text-lg font-bold text-foreground mb-6">
                  Learn More
                </h3>
                <div className="grid gap-4">
                  <a 
                    href="/gap-insurance/what-is-gap-insurance"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">What Is GAP Insurance — and How Was It Mis-Sold?</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                  <a 
                    href="/gap-insurance/fca-action-on-gap-insurance"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">FCA Action: 80% of Providers Suspend Sales</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                  <a 
                    href="/pcp-car-finance-claims"
                    className="p-4 rounded-xl border border-border hover:border-accent/50 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium text-foreground">PCP Car Finance Claims — The Other Scandal</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GAPInsuranceFAQPage;
